/** "Add a category" for the operator's default tag categories.
 *
 *  The operator's categories (and its franchise parent's) are a bounded list, so it is
 *  loaded once (`useTagCategories`, shared with the integration config editor) and
 *  filtered here — a plain Autocomplete, not the remote one (lessons: "RemoteAutocomplete
 *  only fetches on typed input — not for small bounded lists"). Categories that already
 *  have an own row are left out. A category the operator inherits from its franchise
 *  parent is offered as an override of that row: picking it copies every field of the
 *  inherited row, so the licensee starts from what its tours get today.
 *
 *  Options name the category, its id and its owner (the console's tenant + pk rule); a
 *  failed load says so in the open list with Retry. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Autocomplete, Box, Button, TextField, createFilterOptions } from '@mui/material';
import { useMemo, useState } from 'react';

import type { TagCategory } from '@/api/integrations';
import { RecordOption } from '@/components/RemoteAutocomplete/recordOption';
import type {
  AssocCategory,
  AssocRow,
  InheritedRow,
} from '@/features/tag-categories/associations/types';
import { useTagCategories } from '@/features/tour-operators/integrations/config-widgets/hooks';

const filter = createFilterOptions<TagCategory>({
  stringify: (c) => `${c.name} ${c.code} #${c.id}`,
});

export function AddCategoryPicker({
  topId,
  tenantCode,
  parentCode,
  rows,
  inherited,
  onAdd,
  onOverride,
}: {
  topId: number;
  /** This operator's code (own categories). */
  tenantCode: string;
  /** The parent's code (its categories are `is_inherited`). */
  parentCode: string | null;
  rows: readonly AssocRow[];
  inherited: readonly InheritedRow[];
  onAdd: (c: AssocCategory) => void;
  onOverride: (from: InheritedRow) => void;
}) {
  const categories = useTagCategories(topId);
  const [input, setInput] = useState('');

  const inheritedByCategory = useMemo(() => {
    const m = new Map<number, InheritedRow>();
    for (const r of inherited) if (r.category) m.set(r.category.id, r);
    return m;
  }, [inherited]);
  const options = useMemo(() => {
    const own = new Set<number>();
    for (const r of rows) if (r.state !== 'removed' && r.category) own.add(r.category.id);
    return (categories.data ?? []).filter((c) => !own.has(c.id));
  }, [categories.data, rows]);

  const failure = categories.isError ? (
    <Box
      role="alert"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}
      data-autocomplete-error
    >
      <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
      <Box component="span" sx={{ flex: 1 }}>
        Couldn’t load the operator’s tag categories
      </Box>
      <Button
        size="small"
        color="inherit"
        // Keep the focus (and the list) where it is.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => void categories.refetch()}
        sx={{ minWidth: 0, py: 0 }}
      >
        Retry
      </Button>
    </Box>
  ) : null;

  return (
    <Autocomplete<TagCategory, false, false, false>
      size="small"
      options={options}
      value={null}
      inputValue={input}
      onInputChange={(_, v, reason) => setInput(reason === 'reset' ? '' : v)}
      onChange={(_, c) => {
        if (!c) return;
        setInput('');
        const from = inheritedByCategory.get(c.id);
        if (from) onOverride(from);
        else onAdd({ id: c.id, code: c.code, name: c.name });
      }}
      getOptionLabel={(c) => c.name || c.code}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={filter}
      loading={categories.isPending}
      noOptionsText={failure ?? 'Every category already has a row'}
      renderOption={(props, c) => (
        <RecordOption
          key={c.id}
          props={props}
          meta={{
            name: c.name || c.code,
            pk: c.id,
            tenant: c.is_inherited ? parentCode : tenantCode,
            extra: [c.code, inheritedByCategory.has(c.id) ? 'inherited — adds an override' : null],
          }}
        />
      )}
      renderInput={(params) => <TextField {...params} label="Add a category" />}
    />
  );
}
